import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getApi } from '../../Services/apiService';
import PropertyImageModal from '../PropertiesComponent/PropertyDetail/PropertyImageModal';
import {
  fetchLandlordRentLeases,
  fetchPropertyRentHistory,
  formatPaymentAmount,
  formatRentAmount,
  formatRentDate,
  getPaymentStatusInfo,
  getRentStatusInfo,
  resolveLeaseScreeningId,
} from '../../Services/rentService';
import '../../Components/Lease/LeaseAgreement.css';
import '../../Components/Lease/LeaseDetail.css';

const PAGE_SIZE = 10;

const PLACEHOLDER_IMAGE =
  'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800';

/**
 * One lease the landlord collects rent on: the property and tenant on top and
 * the rent payment history from /rent/property-history below. The landlord can
 * hold several leases on the same property, so the route is keyed on the lease
 * id — the row is handed over in navigation state and only re-fetched when the
 * page is opened directly (deep link or refresh).
 */
const LeaseDetail = () => {
  const { id: leaseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [lease, setLease] = useState(location.state?.lease || null);
  const [property, setProperty] = useState(null);
  const [images, setImages] = useState([]);
  const [activeImage, setActiveImage] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [isLoadingProperty, setIsLoadingProperty] = useState(true);

  const [payments, setPayments] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [historyError, setHistoryError] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalItems: 0 });

  const propertyId = lease?.propertyId;

  // Only needed on a direct visit: the table already passes the lease along.
  useEffect(() => {
    if (lease) return;
    if (!leaseId) {
      navigate('/landlord/leases');
      return;
    }

    let cancelled = false;
    const loadLease = async () => {
      try {
        const rows = await fetchLandlordRentLeases();
        if (cancelled) return;
        const match = rows.find((row) => String(row.id) === String(leaseId));
        if (match) {
          setLease(match);
        } else {
          toast.error('That lease could not be found.');
          navigate('/landlord/leases');
        }
      } catch (error) {
        console.error('Error loading lease:', error);
        toast.error(error?.response?.data?.message || 'Failed to load this lease.');
      }
    };

    loadLease();
    return () => {
      cancelled = true;
    };
  }, [lease, leaseId, navigate]);

  // Property header: address, code and the images for the gallery.
  useEffect(() => {
    if (!propertyId) return;

    let cancelled = false;
    const loadProperty = async () => {
      setIsLoadingProperty(true);
      try {
        const response = await getApi(`/property/${propertyId}`);
        if (cancelled) return;
        const data = response?.statusCode === 200 ? response?.data : null;
        if (data) {
          const unit = data.unit_number ? `, ${data.unit_number}` : '';
          setProperty({
            address: `${data.address || ''}${unit}`,
            cityState: [data.city, data.state].filter(Boolean).join(', '),
            zipCode: data.zip_code || '',
            code: data.property_code || '',
            monthlyRent: data.monthly_rent != null ? Number(data.monthly_rent) : null,
          });
          const urls = (data.property_images || []).map((img) => img?.image_url).filter(Boolean);
          setImages(urls.length > 0 ? urls : [PLACEHOLDER_IMAGE]);
        } else {
          setImages([PLACEHOLDER_IMAGE]);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading property for lease detail:', error);
        setImages([PLACEHOLDER_IMAGE]);
      } finally {
        if (!cancelled) setIsLoadingProperty(false);
      }
    };

    loadProperty();
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  const loadHistory = useCallback(
    async (targetPage) => {
      setIsLoadingHistory(true);
      try {
        const result = await fetchPropertyRentHistory({
          propertyId,
          page: targetPage,
          limit: PAGE_SIZE,
        });
        setHistoryError(null);
        setPayments(result.payments);
        setPagination({
          currentPage: result.currentPage,
          totalPages: result.totalPages,
          totalItems: result.totalItems,
        });
      } catch (err) {
        const message =
          err?.response?.data?.message || err?.message || 'Failed to load rent history.';
        console.error('Error loading rent payment history:', err);
        setHistoryError(message);
        setPayments([]);
        toast.error(message);
      } finally {
        setIsLoadingHistory(false);
      }
    },
    [propertyId]
  );

  useEffect(() => {
    if (!propertyId) return;
    loadHistory(page);
  }, [propertyId, page, loadHistory]);

  const [isOpeningTenant, setIsOpeningTenant] = useState(false);

  const openTenantDetails = async () => {
    setIsOpeningTenant(true);
    try {
      const screeningId = await resolveLeaseScreeningId(lease);
      if (screeningId) {
        navigate(`/landlord/properties/${propertyId}/tenant/${screeningId}`);
      } else {
        toast.info("This tenant's application could not be found.");
      }
    } catch (error) {
      console.error('Error opening tenant details:', error);
      toast.error(error?.response?.data?.message || 'Failed to open tenant details.');
    } finally {
      setIsOpeningTenant(false);
    }
  };

  // Full-screen gallery, same modal the property detail page uses.
  const openImageModal = (index) => {
    setModalImageIndex(index);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => setIsImageModalOpen(false);

  const goToNextModalImage = () => {
    setModalImageIndex((prev) => (prev + 1) % images.length);
  };

  const goToPrevModalImage = () => {
    setModalImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const statusInfo = lease ? getRentStatusInfo(lease.status) : null;
  const rentValue = lease?.monthlyRent ?? property?.monthlyRent;
  const address = property?.address || lease?.propertyAddress || 'Property';
  const cityState = property?.cityState || lease?.propertyCityState || '';
  const propertyCode = property?.code || lease?.propertyCode || '';

  const renderHistory = () => {
    if (!propertyId) {
      return <div className="leases-placeholder">Loading lease...</div>;
    }
    if (isLoadingHistory) {
      return <div className="leases-placeholder">Loading rent history...</div>;
    }
    if (historyError) {
      return <div className="leases-placeholder leases-placeholder--error">{historyError}</div>;
    }
    if (payments.length === 0) {
      return (
        <div className="leases-placeholder">No rent payments recorded for this property yet.</div>
      );
    }

    return (
      <table className="screenings-table lease-detail-table">
        <thead>
          <tr>
            <th>Sr. no.</th>
            <th>Payment Date</th>
            <th>Amount</th>
            <th>Billing Period</th>
            <th>Status</th>
            <th>Receipt</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((payment, index) => {
            const paymentStatus = getPaymentStatusInfo(payment.status);
            const period =
              payment.periodStart || payment.periodEnd
                ? `${formatRentDate(payment.periodStart)} – ${formatRentDate(payment.periodEnd)}`
                : '—';
            return (
              <tr key={payment.id ?? `${payment.paidAt}-${index}`}>
                <td>{(pagination.currentPage - 1) * PAGE_SIZE + index + 1}</td>
                <td>{formatRentDate(payment.paidAt)}</td>
                <td>{formatPaymentAmount(payment.amount)}</td>
                <td>{period}</td>
                <td>
                  <span className={`lease-badge lease-badge--${paymentStatus.variant}`}>
                    {paymentStatus.label}
                  </span>
                </td>
                <td>
                  {payment.receiptUrl ? (
                    <a
                      className="leases-link-button"
                      href={payment.receiptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View
                    </a>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div className="screenings-page lease-detail-page">
      <div className="lease-detail-card">
        <div className="lease-detail-gallery">
          {isLoadingProperty ? (
            <div className="lease-detail-image lease-detail-image--loading" />
          ) : (
            <img
              src={images[activeImage] || PLACEHOLDER_IMAGE}
              alt={address}
              className="lease-detail-image"
              onClick={() => openImageModal(activeImage)}
              onError={(e) => {
                e.currentTarget.src = PLACEHOLDER_IMAGE;
              }}
            />
          )}
          {images.length > 1 ? (
            <div className="lease-detail-thumbs">
              {images.map((url, index) => (
                <button
                  key={url}
                  type="button"
                  className={`lease-detail-thumb ${index === activeImage ? 'is-active' : ''}`}
                  onClick={() => setActiveImage(index)}
                  aria-label={`Show image ${index + 1}`}
                >
                  <img src={url} alt="" />
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="lease-detail-info">
          <h1 className="lease-detail-address">{address}</h1>
          <p className="lease-detail-location">
            {[cityState, property?.zipCode].filter(Boolean).join(' ')}
            {propertyCode ? `${cityState || property?.zipCode ? ' · ' : ''}Code: ${propertyCode}` : ''}
          </p>

          <div className="lease-detail-meta">
            <div className="lease-detail-meta-item">
              <span className="lease-detail-meta-label">Tenant</span>
              <span className="lease-detail-meta-value">{lease?.tenantName || '—'}</span>
              {lease?.tenantEmail ? (
                <span className="leases-cell-sub">{lease.tenantEmail}</span>
              ) : null}
            </div>
            <div className="lease-detail-meta-item">
              <span className="lease-detail-meta-label">Monthly Rent</span>
              <span className="lease-detail-meta-value">{formatRentAmount(rentValue)}</span>
            </div>
            <div className="lease-detail-meta-item">
              <span className="lease-detail-meta-label">Start Date</span>
              <span className="lease-detail-meta-value">{formatRentDate(lease?.startDate)}</span>
            </div>
            <div className="lease-detail-meta-item">
              <span className="lease-detail-meta-label">Next Payment</span>
              <span className="lease-detail-meta-value">
                {formatRentDate(lease?.nextPaymentDate)}
              </span>
            </div>
            <div className="lease-detail-meta-item">
              <span className="lease-detail-meta-label">Status</span>
              <span className="lease-detail-meta-value">
                {statusInfo ? (
                  <span className={`lease-badge lease-badge--${statusInfo.variant}`}>
                    {statusInfo.label}
                  </span>
                ) : (
                  '—'
                )}
              </span>
            </div>
          </div>

          <div className="lease-detail-actions">
            <button
              type="button"
              className="lease-btn lease-btn--secondary"
              onClick={() =>
                navigate(`/landlord/properties/${propertyId}`, { state: { fromLease: true } })
              }
              disabled={!propertyId}
            >
              View Property Details
            </button>
            <button
              type="button"
              className="lease-btn lease-btn--secondary"
              onClick={openTenantDetails}
              disabled={!propertyId || (!lease?.tenantId && !lease?.tenantEmail) || isOpeningTenant}
            >
              View Tenant Details
            </button>
          </div>
        </div>
      </div>

      <h2 className="screenings-title">
        Rent Payment History
        {!isLoadingHistory && !historyError && pagination.totalItems
          ? ` (${pagination.totalItems})`
          : ''}
      </h2>

      <div className="screenings-content">{renderHistory()}</div>

      {!isLoadingHistory && !historyError && pagination.totalPages > 1 ? (
        <div className="rent-history-pager">
          <button
            type="button"
            className="lease-btn lease-btn--secondary"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={pagination.currentPage <= 1}
          >
            Previous
          </button>
          <span className="rent-history-pager-label">
            Page {pagination.currentPage} of {pagination.totalPages}
          </span>
          <button
            type="button"
            className="lease-btn lease-btn--secondary"
            onClick={() => setPage((current) => current + 1)}
            disabled={pagination.currentPage >= pagination.totalPages}
          >
            Next
          </button>
        </div>
      ) : null}

      <PropertyImageModal
        isOpen={isImageModalOpen}
        onClose={closeImageModal}
        images={images}
        currentIndex={modalImageIndex}
        onNext={goToNextModalImage}
        onPrev={goToPrevModalImage}
        onIndicatorClick={setModalImageIndex}
      />
    </div>
  );
};

export default LeaseDetail;
